/**
 * 定位服务 - 使用Geolocation API获取用户位置
 * 支持GPS和网络定位，自动选择最佳方式
 */

import type { GeoLocation } from '../types';

/**
 * 获取当前位置
 * @param highAccuracy 是否使用高精度模式
 * @param timeout 超时时间（毫秒）
 */
export async function getCurrentLocation(
  highAccuracy: boolean = true,
  timeout: number = 10000
): Promise<GeoLocation> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('浏览器不支持定位功能'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location: GeoLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          timestamp: position.timestamp,
          provider: position.coords.accuracy < 20 ? 'gps' : 'network'
        };
        resolve(location);
      },
      async (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(new Error('定位权限被拒绝'));
            break;
          case error.POSITION_UNAVAILABLE:
            reject(new Error('无法获取位置信息'));
            break;
          case error.TIMEOUT:
            reject(new Error('定位请求超时'));
            break;
          default:
            reject(new Error('定位失败'));
        }
      },
      {
        enableHighAccuracy: highAccuracy,
        timeout: timeout,
        maximumAge: 0 // 总是获取最新位置
      }
    );
  });
}

/**
 * 监听位置变化
 * 返回一个取消函数
 */
export function watchLocation(
  onSuccess: (location: GeoLocation) => void,
  onError: (error: string) => void,
  highAccuracy: boolean = true
): () => void {
  if (!navigator.geolocation) {
    onError('浏览器不支持定位功能');
    return () => {};
  }

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      const location: GeoLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        timestamp: position.timestamp,
        provider: position.coords.accuracy < 20 ? 'gps' : 'network'
      };
      onSuccess(location);
    },
    (error) => {
      onError(error.message);
    },
    {
      enableHighAccuracy: highAccuracy,
      timeout: 10000,
      maximumAge: 5000
    }
  );

  // 返回取消函数
  return () => {
    navigator.geolocation.clearWatch(watchId);
  };
}

/**
 * 检查定位权限状态
 */
export async function checkLocationPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!navigator.permissions) {
    // 浏览器不支持permissions API，假设需要请求
    return 'prompt';
  }

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state as 'granted' | 'denied' | 'prompt';
  } catch {
    return 'prompt';
  }
}

/**
 * 格式化位置为可读字符串
 */
export function formatLocation(location: GeoLocation | null): string {
  if (!location || location.latitude === 0 && location.longitude === 0) {
    return '位置未知';
  }

  if (location.accuracy < 0) {
    return '定位失败';
  }

  // 精确度分级
  const accuracyLevel = location.accuracy < 10 ? '高' :
                         location.accuracy < 50 ? '中' : '低';

  return `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)} (${accuracyLevel}精度)`;
}

/**
 * 生成位置地图链接（高德/百度/Google）
 */
export function getLocationMapLink(location: GeoLocation, type: 'amap' | 'baidu' | 'google' = 'amap'): string {
  const { latitude, longitude } = location;

  switch (type) {
    case 'amap':
      return `https://restapi.amap.com/v3/staticmap?location=${longitude},${latitude}&zoom=15&size=400*300&markers=mid,,A:${longitude},${latitude}`;
    case 'baidu':
      return `https://api.map.baidu.com/marker?location=${latitude},${longitude}&title=求救位置&content=紧急求助位置&output=html`;
    case 'google':
      return `https://www.google.com/maps?q=${latitude},${longitude}`;
    default:
      return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=15`;
  }
}
